import { CbhIcon, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Skeleton, Stack, useTheme } from "@mui/material";
import { useGetClipboardScoreProfile } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useCurrentDate } from "@src/appV2/lib/utils/useCurrentDate";
import {
  isWorkerInviteRequired,
  isWorkerRestricted,
} from "@src/appV2/redesign/ClipboardScore/utils/utils";
import { useGetWorker } from "@src/appV2/Worker/api/useGetWorker";
import { differenceInMinutes, parseISO } from "date-fns";
import { useMemo } from "react";

import { formatNextIncreaseDate } from "./formatNextIncreaseDate";
import { computeCancellationPenaltyPreview } from "./useCancellationPenaltyPreview";

const REFRESH_INTERVAL_MS = 60_000;

interface CancellationPenaltyFooterProps {
  shiftId: string;
  shiftStart: string;
  workplaceId: string | undefined;
  qualification?: string;
}

export interface CancellationPenaltyFooterContentProps {
  cancelNowLabel: string;
  nextBracketLabel?: { timeLabel: string; penaltyLabel: string };
  isLoading?: boolean;
}

export function CancellationPenaltyFooterContent(props: CancellationPenaltyFooterContentProps) {
  const { cancelNowLabel, nextBracketLabel, isLoading = false } = props;
  const theme = useTheme();

  if (isLoading) {
    return <Skeleton variant="rounded" height={20} />;
  }

  return (
    <Stack spacing={1}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" alignItems="center" spacing={1}>
          <CbhIcon type="remove" size="small" color={(iconTheme) => iconTheme.palette.error.main} />
          <Text variant="caption" sx={{ color: theme.palette.error.main }}>
            Cancel now:
          </Text>
        </Stack>
        <Text semibold variant="caption" sx={{ color: theme.palette.error.main }}>
          {cancelNowLabel}
        </Text>
      </Stack>

      {nextBracketLabel && (
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" spacing={1}>
            <CbhIcon
              type="clock"
              size="small"
              color={(iconTheme) => iconTheme.palette.text.tertiary}
            />
            <Text variant="caption" sx={{ color: theme.palette.text.tertiary }}>
              {nextBracketLabel.timeLabel}
            </Text>
          </Stack>
          <Text semibold variant="caption" sx={{ color: theme.palette.text.tertiary }}>
            {nextBracketLabel.penaltyLabel}
          </Text>
        </Stack>
      )}
    </Stack>
  );
}

export function CancellationPenaltyFooter(props: CancellationPenaltyFooterProps) {
  const { shiftId, shiftStart, workplaceId, qualification } = props;

  const { data: worker } = useGetWorker();

  const isCancellationPenaltyPreviewEnabled = useCbhFlag(
    CbhFeatureFlag.CANCELLATION_PENALTY_PREVIEW,
    { defaultValue: false }
  );

  const dynamicPenaltiesConfig = useCbhFlag(CbhFeatureFlag.DYNAMIC_PENALTIES_CONFIG, {
    defaultValue: { enabled: false },
  });

  const emergencyCreditsConfig = useCbhFlag(CbhFeatureFlag.EMERGENCY_CREDITS_CONFIG, {
    defaultValue: { enabled: false },
  });

  const hasExperimentalAttendancePolicies =
    dynamicPenaltiesConfig.enabled || emergencyCreditsConfig.enabled;

  const isEnabled =
    isCancellationPenaltyPreviewEnabled &&
    !hasExperimentalAttendancePolicies &&
    isDefined(worker) &&
    !isWorkerRestricted(worker) &&
    !isWorkerInviteRequired(worker);

  const {
    data: clipboardScoreProfile,
    isLoading,
    isSuccess,
  } = useGetClipboardScoreProfile(
    worker?.userId ?? "",
    {
      enabled: isEnabled,
    },
    qualification,
    workplaceId
  );

  const currentDate = useCurrentDate(REFRESH_INTERVAL_MS);

  const preview = useMemo(() => {
    if (!isSuccess || !isDefined(clipboardScoreProfile)) {
      return undefined;
    }

    return computeCancellationPenaltyPreview({
      clipboardScoreProfile,
      shiftStart,
      currentDate,
    });
  }, [clipboardScoreProfile, isSuccess, shiftStart, currentDate]);

  const isFooterVisible = isEnabled && isDefined(preview) && preview.isVisible;

  useLogEffect(
    APP_V2_USER_EVENTS.MY_BOOKINGS_CANCELLATION_PENALTY_FOOTER_VIEWED,
    {
      workerId: worker?.userId,
      shiftId,
      leadTimeHours: differenceInMinutes(parseISO(shiftStart), currentDate) / 60,
      workerCurrentScore: clipboardScoreProfile?.clipboardScore,
      workerScoreIfCancelNow: preview?.cancelNowIsRestriction
        ? 0
        : (clipboardScoreProfile?.clipboardScore ?? 0) + (preview?.cancelNowPoints ?? 0),
      workerScoreIfCancelLater: preview?.nextBracketIsRestriction
        ? 0
        : (clipboardScoreProfile?.clipboardScore ?? 0) + (preview?.nextBracketPoints ?? 0),
    },
    { enabled: isFooterVisible }
  );

  if (!isEnabled) {
    return null;
  }

  if (isLoading) {
    return <CancellationPenaltyFooterContent isLoading cancelNowLabel="" />;
  }

  if (!isFooterVisible) {
    return null;
  }

  const cancelNowLabel = preview.cancelNowIsRestriction
    ? "Account Restriction"
    : `${preview.cancelNowPoints} pts`;

  const nextBracketLabel = getNextBracketLabel(preview, currentDate);

  // Hide the "Cancel after" row when both rows show the same outcome
  const showNextBracket =
    isDefined(nextBracketLabel) &&
    (preview.cancelNowIsRestriction !== nextBracketLabel.isRestriction ||
      preview.cancelNowPoints !== preview.nextBracketPoints);

  return (
    <CancellationPenaltyFooterContent
      cancelNowLabel={cancelNowLabel}
      nextBracketLabel={showNextBracket ? nextBracketLabel : undefined}
    />
  );
}

function getNextBracketLabel(
  preview: {
    nextIncreaseDate: Date | undefined;
    nextBracketPoints: number | undefined;
    nextBracketIsRestriction: boolean;
  },
  currentDate: Date
): { timeLabel: string; penaltyLabel: string; isRestriction: boolean } | undefined {
  if (preview.nextBracketIsRestriction && !isDefined(preview.nextIncreaseDate)) {
    return {
      timeLabel: "Cancel after start time",
      penaltyLabel: "Account Restriction",
      isRestriction: true,
    };
  }

  if (isDefined(preview.nextIncreaseDate)) {
    const timeLabel = `Cancel after ${formatNextIncreaseDate(preview.nextIncreaseDate, currentDate)}`;
    if (preview.nextBracketIsRestriction) {
      return {
        timeLabel,
        penaltyLabel: "Account Restriction",
        isRestriction: true,
      };
    }

    return {
      timeLabel,
      penaltyLabel: `${preview.nextBracketPoints} pts`,
      isRestriction: false,
    };
  }

  return undefined;
}
